import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react'

interface Toast {
  id: number
  message: string
  kind: 'info' | 'success' | 'error'
}

interface ToastContextValue {
  toast: (message: string, kind?: Toast['kind']) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)
let counter = 0

/**
 * Info and success toasts clear themselves after a few seconds. Error toasts stay until
 * dismissed (WCAG 2.2.1) and are announced assertively (4.1.3); the others are polite.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const dismiss = useCallback((id: number) => {
    setToasts((t) => t.filter((x) => x.id !== id))
  }, [])
  const toast = useCallback(
    (message: string, kind: Toast['kind'] = 'info') => {
      const id = ++counter
      setToasts((t) => [...t, { id, message, kind }])
      if (kind !== 'error') window.setTimeout(() => dismiss(id), 6000)
    },
    [dismiss],
  )
  const value = useMemo(() => ({ toast }), [toast])
  const errors = toasts.filter((t) => t.kind === 'error')
  const others = toasts.filter((t) => t.kind !== 'error')
  const item = (t: Toast) => (
    <div key={t.id} className={`toast toast--${t.kind}`}>
      <span>{t.message}</span>
      <button
        type="button"
        className="toast__close"
        onClick={() => dismiss(t.id)}
        aria-label="Dismiss notification"
      >
        ✕
      </button>
    </div>
  )
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-region">
        <div role="status" aria-live="polite">
          {others.map(item)}
        </div>
        <div role="alert">{errors.map(item)}</div>
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const v = useContext(ToastContext)
  if (!v) throw new Error('useToast must be used inside ToastProvider')
  return v.toast
}
